/**
 * The team an admin account is observing, if any (#2060).
 *
 * An account on the admin allowlist can open any team; on a team it holds
 * no membership on, `GET /api/teams/{id}` answers `role: "observer"`
 * (#2059). Observation follows the address: while the URL names
 * `/teams/:tid/…` for a team that is not in the caller's own list, this
 * reads the team (the same cached query the team pages make) and returns
 * it when the API says `observer`. Anywhere else it returns null, and the
 * shell is the caller's own as before.
 *
 * Non-admin accounts never fetch anything here.
 */
import { useQuery } from "@tanstack/react-query";
import { useContext } from "react";
import { useLocation } from "react-router-dom";

import { AuthContext } from "../lib/AuthContext";
import { isObserverRole } from "../lib/teamAccess";
import { apiFetch } from "./client";
import type { TeamSummary } from "./useTeam";
import { useWorkspaces } from "./useWorkspaces";

const TEAM_ADDRESS = /^\/teams\/([0-9a-f-]{36})(?:\/|$)/i;

export interface ObservedTeam {
  id: string;
  name: string;
  teamKind: "scheduling" | "tasks";
}

/** The team id in the address, when there is one. */
export function teamIdFromPath(pathname: string): string | null {
  return pathname.match(TEAM_ADDRESS)?.[1] ?? null;
}

export function useObservedTeam(): ObservedTeam | null {
  // The context directly (not `useAuth`): the team switcher calls this and
  // renders in harnesses without an AuthProvider.
  const auth = useContext(AuthContext);
  const account = auth?.account ?? null;
  const accountId = auth?.accountId ?? null;
  const { pathname } = useLocation();
  const workspaces = useWorkspaces().data;
  const addressTeamId = teamIdFromPath(pathname);
  const candidate =
    account?.is_admin === true &&
    addressTeamId &&
    workspaces &&
    !workspaces.some((w) => w.team_id === addressTeamId)
      ? addressTeamId
      : undefined;
  // The same query (key and read) as `useTeam`, so the team pages share it.
  const team = useQuery({
    queryKey: ["team", candidate ?? "", accountId ?? ""],
    enabled: Boolean(candidate && accountId),
    staleTime: 60_000,
    retry: false,
    queryFn: () => apiFetch<TeamSummary>(`/api/teams/${candidate}`),
  }).data;
  if (!candidate || !team || team.id !== candidate || !isObserverRole(team.role)) return null;
  return {
    id: team.id,
    name: team.name,
    teamKind: team.team_kind === "tasks" ? "tasks" : "scheduling",
  };
}
